/* Clienti: l'anagrafica su cui si imputa e si fattura.
 *
 * **Il regime fiscale del cliente non e' un dettaglio anagrafico**, e questa
 * pagina lo tratta per quello che e': decide se sulla parcella c'e' la
 * ritenuta d'acconto, se c'e' l'IVA, e come. Sbagliarlo qui produce un
 * documento credibile e sbagliato, che e' il difetto peggiore di tutti perche'
 * nessuno lo riconosce guardandolo.
 *
 * Per questo ogni interruttore porta scritto cosa cambia, invece di chiamarsi
 * col suo nome tecnico e basta.
 *
 * **La ricerca, dal 25 settembre 2026**, arriva prima dei clienti veri e non
 * dopo: con circa cinquecento clienti un elenco da scorrere non si usa, e il
 * primo giorno di uso vero sarebbe anche l'ultimo. Si cerca su tutto quello
 * che una persona dello studio ha in mano quando cerca un cliente (nome,
 * codice interno, codice fiscale, partita IVA, sede), e il filtro sulle
 * anagrafiche incomplete serve a ripulire subito dopo un'importazione.
 *
 * **Le pratiche si chiudono, non si cancellano.** Chiusa non vuol dire
 * fatturata: sono due assi diversi, e tenerli insieme e' il modo di perdere di
 * vista il maturato di una pratica finita e mai incassata.
 */

import { useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ErroreApi, anagrafica } from "../api";
import { useCarica } from "../carica";
import { FatturatoCliente } from "../componenti/FatturatoCliente";
import { Interruttore } from "../componenti/Interruttore";
import { TestaPagina } from "../componenti/TestaPagina";
import { ErroreCampo, attributiErrore } from "../componenti/ErroreCampo";
import { useSessione } from "../sessione";
import {
  TERRITORIALITA,
  TIPI_PRATICA,
  conta,
  euro,
  importoDaScritto,
  nomeStato,
  nomeTipo,
  oggi,
  type Cliente,
  type Pratica,
} from "../dominio";

export function Clienti() {
  const clienti = useCarica<Cliente[]>(() => anagrafica.clienti(), []);
  const pratiche = useCarica<Pratica[]>(() => anagrafica.pratiche(), []);
  const [scelto, setScelto] = useState<string | null>(null);
  const [parametri, setParametri] = useSearchParams();
  const nuovo = parametri.get("nuovo") === "1";
  function setNuovo(apri: boolean) {
    setParametri((attuali) => {
      const prossimi = new URLSearchParams(attuali);
      if (apri) prossimi.set("nuovo", "1");
      else prossimi.delete("nuovo");
      return prossimi;
    });
  }
  const [cerca, setCerca] = useState("");
  const [soloIncompleti, setSoloIncompleti] = useState(false);

  const errore = clienti.errore ?? pratiche.errore;
  const tutti = [...(clienti.dati ?? [])].sort((a, b) =>
    a.denominazione.localeCompare(b.denominazione),
  );
  const trovati = tutti.filter(
    (c) => corrisponde(c, cerca) && (!soloIncompleti || anagraficaIncompleta(c)),
  );
  // Si apre sempre un cliente, il primo dell'elenco se non se ne e' scelto
  // uno: una scheda vuota a destra sarebbe spazio sprecato.
  const cliente = trovati.find((c) => c.identificativo === scelto) ?? trovati[0];
  const praticheDi = (id: string) =>
    (pratiche.dati ?? []).filter((p) => p.cliente.identificativo === id);

  return (
    <>
      <TestaPagina titolo="Clienti" />

      {errore && (
        <p className="errore" role="alert">
          {errore}
        </p>
      )}

      {clienti.dati === undefined ? (
        <p className="attesa">Un momento.</p>
      ) : (
        <div className="due-colonne">
          <aside className="elenco-laterale" aria-label="Elenco dei clienti">
            <button
              type="button"
              className="tasto tasto-principale tasto-pieno"
              onClick={() => setNuovo(true)}
            >
              Aggiungi un cliente
            </button>
            <input
              type="search"
              className="cerca-laterale"
              aria-label="Cerca un cliente"
              value={cerca}
              placeholder="cerca nome, codice, partita IVA"
              onChange={(e) => setCerca(e.target.value)}
            />
            <label className="spunta">
              <input
                type="checkbox"
                checked={soloIncompleti}
                onChange={(e) => setSoloIncompleti(e.target.checked)}
              />{" "}
              solo anagrafiche incomplete
            </label>
            <p className="aiuto">
              {cerca.trim() || soloIncompleti
                ? `${trovati.length} di ${conta(tutti.length, "cliente", "clienti")}`
                : conta(tutti.length, "cliente", "clienti")}
            </p>
            <ul className="voci-laterali">
              {trovati.map((c) => (
                <li key={c.identificativo}>
                  <button
                    type="button"
                    aria-current={!nuovo && c.identificativo === cliente?.identificativo}
                    onClick={() => {
                      setScelto(c.identificativo);
                      setNuovo(false);
                    }}
                  >
                    <span className="voce-nome">{c.denominazione}</span>
                    <span className="voce-sotto">
                      {conta(praticheDi(c.identificativo).filter((p) => p.stato === "APERTA").length, "pratica aperta", "pratiche aperte")}
                      {anagraficaIncompleta(c) && (
                        <span className="segno-ritardo voce-avviso">anagrafica da completare</span>
                      )}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
            {trovati.length === 0 && tutti.length > 0 && (
              <p className="aiuto">Nessun cliente corrisponde.</p>
            )}
          </aside>

          <section className="foglio">
            {nuovo ? (
              <ModuloCliente
                chiudi={() => setNuovo(false)}
                dopo={async (creato) => {
                  await clienti.ricarica();
                  setScelto(creato);
                  setNuovo(false);
                }}
              />
            ) : cliente ? (
              <SchedaCliente
                key={cliente.identificativo}
                cliente={cliente}
                pratiche={praticheDi(cliente.identificativo)}
                dopo={pratiche.ricarica}
                dopoCliente={clienti.ricarica}
              />
            ) : tutti.length > 0 ? (
              <div className="foglio-vuoto-solo">
                <h2>Nessun cliente corrisponde</h2>
                <p>Cambia la ricerca o togli il filtro sulle anagrafiche incomplete.</p>
                <button type="button" className="tasto" onClick={() => { setCerca(""); setSoloIncompleti(false); }}>Mostra tutti i clienti</button>
              </div>
            ) : (
              <div className="foglio-vuoto-solo">
                <h2>Aggiungi il primo cliente</h2>
                <p>Da qui partiranno le ore e gli avvisi dello studio. Puoi completare i dati per i documenti anche dopo.</p>
                <button type="button" className="tasto tasto-principale" onClick={() => setNuovo(true)}>Aggiungi un cliente</button>
              </div>
            )}
          </section>
        </div>
      )}
    </>
  );
}

// ------------------------------------------- cliente nuovo o da correggere

/** Lo stesso modulo per aggiungere un cliente e per correggerne l'anagrafica,
 * recapito per Fatture Smart compreso: prima, correggendo, spariva e si
 * cambiava solo dalla scheda. Per una pubblica amministrazione non c'e' il
 * codice destinatario di 7 caratteri ma il codice univoco dell'ufficio, di 6. */
function ModuloCliente({
  iniziale,
  dopo,
  chiudi,
}: {
  iniziale?: Cliente;
  dopo: (identificativo: string) => Promise<void>;
  chiudi: () => void;
}) {
  const [denominazione, setDenominazione] = useState(iniziale?.denominazione ?? "");
  const [codice, setCodice] = useState(iniziale?.codice ?? "");
  const [sede, setSede] = useState(iniziale?.sede ?? "");
  const [indirizzo, setIndirizzo] = useState(iniziale?.indirizzo ?? "");
  const [codiceFiscale, setCodiceFiscale] = useState(iniziale?.codice_fiscale ?? "");
  const [partitaIva, setPartitaIva] = useState(iniziale?.partita_iva ?? "");
  const [codiceDestinatario, setCodiceDestinatario] = useState(iniziale?.codice_destinatario ?? "");
  const [pec, setPec] = useState(iniziale?.pec ?? "");
  const [sostituto, setSostituto] = useState<boolean | null>(iniziale?.sostituto_imposta ?? null);
  const [pa, setPa] = useState(iniziale?.pubblica_amministrazione ?? false);
  const [split, setSplit] = useState(iniziale?.split_payment ?? false);
  const [territorialita, setTerritorialita] = useState<string>(iniziale?.territorialita ?? "ITALIA");
  const [errore, setErrore] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);
  const [campi, setCampi] = useState<Record<string, string>>({});
  const erroreCampo = (campo: string) => <ErroreCampo campo={campo} messaggio={campi[campo]} />;

  async function invia(evento: FormEvent<HTMLFormElement>) {
    evento.preventDefault();
    const modulo = evento.currentTarget;
    setErrore(null);
    setCampi({});
    const mancanti: Record<string, string> = {};
    if (!denominazione.trim()) mancanti.denominazione = "Scrivi il nome del cliente.";
    if (sostituto === null) mancanti.sostituto_imposta = "Scegli Sì o No prima di aggiungere il cliente.";
    const lunghezzaCodice = pa ? 6 : 7;
    if (codiceDestinatario.trim() && !new RegExp(`^[a-z0-9]{${lunghezzaCodice}}$`, "i").test(codiceDestinatario.trim())) {
      mancanti.codice_destinatario = pa
        ? "Il codice univoco dell'ufficio ha 6 lettere o cifre."
        : "Il codice destinatario ha 7 lettere o cifre. Per una pubblica amministrazione spunta «È pubblica amministrazione».";
    }
    if (!pa && pec.trim() && !pec.includes("@")) mancanti.pec = "Inserisci un indirizzo PEC con la chiocciola.";
    const segnala = (errori: Record<string, string>) => {
      setCampi(errori);
      if (errori.pec || errori.codice_destinatario) {
        const recapito = modulo.querySelector<HTMLDetailsElement>("details");
        if (recapito) recapito.open = true;
      }
      requestAnimationFrame(() => modulo.querySelector<HTMLElement>('input[aria-invalid="true"], select[aria-invalid="true"]')?.focus());
    };
    if (Object.keys(mancanti).length > 0 || sostituto === null) {
      segnala(mancanti);
      setErrore("Controlla i campi indicati.");
      return;
    }
    setInCorso(true);
    try {
      if (iniziale !== undefined) {
        await anagrafica.correggiCliente(iniziale.identificativo, {
          denominazione,
          codice,
          sede,
          indirizzo,
          codice_fiscale: codiceFiscale,
          partita_iva: partitaIva,
          codice_destinatario: codiceDestinatario,
          pec: pa ? "" : pec,
          sostituto_imposta: sostituto,
          pubblica_amministrazione: pa,
          split_payment: split,
          territorialita,
        });
        await dopo(iniziale.identificativo);
        return;
      }
      const creato = await anagrafica.creaCliente({
        denominazione,
        codice,
        sede,
        indirizzo,
        codice_fiscale: codiceFiscale,
        partita_iva: partitaIva,
        codice_destinatario: codiceDestinatario,
        pec: pa ? "" : pec,
        sostituto_imposta: sostituto,
        pubblica_amministrazione: pa,
        split_payment: split,
        territorialita,
      });
      setDenominazione("");
      setCodice("");
      setSede("");
      setIndirizzo("");
      setCodiceFiscale("");
      setPartitaIva("");
      setCodiceDestinatario("");
      setPec("");
      await dopo(creato.identificativo);
    } catch (e) {
      if (e instanceof ErroreApi) segnala(e.campi);
      setErrore(e instanceof ErroreApi ? e.message : "Non sono riuscito a salvare.");
    } finally {
      setInCorso(false);
    }
  }

  return (
    <form className="modulo" onSubmit={invia} noValidate>
      <h2>{iniziale ? "Correggi l'anagrafica" : "Cliente nuovo"}</h2>
      {iniziale && (
        <p className="aiuto">
          I documenti già creati restano con i dati di quando sono nati. Un avviso ancora in
          bozza con i dati vecchi si annulla e si rifà.
        </p>
      )}

      <fieldset className="gruppo-modulo">
      <legend>Chi è il cliente</legend>
      <div className="campo">
        <label htmlFor="denominazione">Denominazione</label>
        <input
          id="denominazione"
          {...attributiErrore("denominazione", campi)}
          value={denominazione}
          required
          autoFocus
          onChange={(e) => setDenominazione(e.target.value)}
        />
        {erroreCampo("denominazione")}
      </div>

      <div className="imputa-riga">
        <div className="campo">
          <label htmlFor="codice">Codice interno</label>
          <input id="codice" value={codice} onChange={(e) => setCodice(e.target.value)} />
        </div>
        <div className="campo campo-largo">
          <label htmlFor="sede">Sede</label>
          <input id="sede" value={sede} onChange={(e) => setSede(e.target.value)} />
        </div>
      </div>

      </fieldset>
      <fieldset className="gruppo-modulo">
      <legend>Dati per i documenti</legend>
      <div className="campo">
        <label htmlFor="indirizzo">Indirizzo, come compare sul documento</label>
        <input
          id="indirizzo"
          {...attributiErrore("indirizzo", campi)}
          value={indirizzo}
          placeholder="Via Roma 1, 10121 Torino (TO)"
          onChange={(e) => setIndirizzo(e.target.value)}
        />
        {erroreCampo("indirizzo")}
      </div>

      <div className="imputa-riga">
        <div className="campo">
          <label htmlFor="codice-fiscale">Codice fiscale</label>
          <input
            id="codice-fiscale"
            {...attributiErrore("codice_fiscale", campi)}
            value={codiceFiscale}
            onChange={(e) => setCodiceFiscale(e.target.value)}
          />
          {erroreCampo("codice_fiscale")}
        </div>
        <div className="campo">
          <label htmlFor="partita-iva">Partita IVA, se un'impresa</label>
          <input
            id="partita-iva"
            {...attributiErrore("partita_iva", campi)}
            value={partitaIva}
            onChange={(e) => setPartitaIva(e.target.value)}
          />
          {erroreCampo("partita_iva")}
        </div>
      </div>
      <p className="aiuto">
        Una persona fisica o un ente pubblico hanno di solito solo il codice
        fiscale. Servono per stampare un documento: senza nessuno dei due, la
        stampa si rifiuta.
      </p>
      </fieldset>

      <details className="recapito-facoltativo" open={Boolean(iniziale)}>
      <summary>Recapito per Fatture Smart <span className="aiuto">facoltativo, anche dopo</span></summary>
      <div className="imputa-riga">
        <div className="campo">
          <label htmlFor="codice-destinatario">
            {pa ? "Codice univoco ufficio" : "Codice destinatario SDI"}
          </label>
          <input
            id="codice-destinatario"
            {...attributiErrore("codice_destinatario", campi)}
            value={codiceDestinatario}
            maxLength={pa ? 6 : 7}
            onChange={(e) => setCodiceDestinatario(e.target.value)}
          />
          {erroreCampo("codice_destinatario")}
        </div>
        {!pa && (
          <div className="campo campo-largo">
            <label htmlFor="pec">oppure PEC</label>
            <input id="pec" {...attributiErrore("pec", campi)} value={pec} onChange={(e) => setPec(e.target.value)} />
            {erroreCampo("pec")}
          </div>
        )}
      </div>
      <p className="aiuto">
        {pa
          ? "Una pubblica amministrazione non ha il codice destinatario né la PEC: la fattura elettronica si recapita con il codice univoco dell'ufficio, di 6 caratteri."
          : "Dove lo SDI consegna la fattura elettronica: 7 caratteri, oppure la PEC."}{" "}
        Finisce nella scheda da copiare in Fatture Smart; si può aggiungere anche dopo.
      </p>
      </details>

      <fieldset className="gruppo-modulo">
      <legend>Come si fattura</legend>
      <div className="campo">
        <label htmlFor="territorialita">Dove risiede, ai fini IVA</label>
        <select
          id="territorialita"
          value={territorialita}
          onChange={(e) => setTerritorialita(e.target.value)}
        >
          {TERRITORIALITA.map((t) => (
            <option key={t.valore} value={t.valore}>
              {t.nome}
            </option>
          ))}
        </select>
        <p className="aiuto">
          Un privato residente in UE si fattura come un italiano. Uno fuori UE
          riceve un documento fuori campo IVA, ma se la prestazione riguarda un
          immobile in Italia l'IVA è comunque dovuta: quel caso va guardato riga
          per riga.
        </p>
      </div>

      <fieldset className="interruttori">
        <legend>Scelte per gli avvisi</legend>

        <fieldset className="scelta-fiscale" aria-describedby="aiuto-sostituto">
          <legend>È sostituto d'imposta?</legend>
          <p id="aiuto-sostituto" className="aiuto">Questa scelta determina se proporre la ritenuta negli avvisi.</p>
          <div className="scelte-fiscali">
            {[true, false].map((valore) => <label key={String(valore)}>
              <input type="radio" name="sostituto-imposta" value={String(valore)}
                {...attributiErrore("sostituto_imposta", campi)}
                checked={sostituto === valore} onChange={() => setSostituto(valore)} />
              {valore ? "Sì" : "No"}
            </label>)}
          </div>
          {erroreCampo("sostituto_imposta")}
        </fieldset>

        <Interruttore
          titolo="È pubblica amministrazione"
          spiegazione="Cambia la numerazione della parcella, che prende la serie dedicata."
          acceso={pa}
          cambia={setPa}
        />

        <Interruttore
          titolo="Scissione dei pagamenti"
          spiegazione="Raro: sulle parcelle dello studio viste finora l'IVA verso la pubblica amministrazione è esposta normalmente. Lascialo spento se non sei sicuro."
          acceso={split}
          cambia={setSplit}
        />
      </fieldset>
      </fieldset>

      {errore && (
        <p className="errore" role="alert">
          {errore}
        </p>
      )}

      <div className="tasti">
        <button className="tasto tasto-principale" type="submit" disabled={inCorso}>
          {inCorso ? "Sto salvando" : iniziale ? "Salva l'anagrafica" : "Aggiungi il cliente"}
        </button>
        <button type="button" className="tasto tasto-nudo" onClick={chiudi}>
          Lascia perdere
        </button>
      </div>
    </form>
  );
}

// ----------------------------------------------------------- scheda cliente

interface SchedaProps {
  cliente: Cliente;
  pratiche: Pratica[];
  /** Ricarica le pratiche: e' quello che cambia di solito dentro una scheda. */
  dopo: () => Promise<void>;
  /** Ricarica i clienti: serve quando cambia il cliente stesso (il recapito SDI). */
  dopoCliente: () => Promise<void>;
}

/** Minuscolo e senza accenti: "Società" si trova scrivendo "societa". */
function normale(testo: string): string {
  return testo.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
}

/** Ogni parola cercata deve comparire da qualche parte nel cliente. I
 * codici si confrontano anche senza spazi, perché una partita IVA si copia
 * spesso da un documento che la spezza in gruppi. */
function corrisponde(cliente: Cliente, cerca: string): boolean {
  const parole = normale(cerca).split(/\s+/).filter(Boolean);
  if (parole.length === 0) return true;
  const campi = normale(
    [
      cliente.denominazione,
      cliente.codice,
      cliente.codice_fiscale,
      cliente.partita_iva,
      cliente.sede,
      cliente.indirizzo,
    ].join(" "),
  );
  const compatti = campi.replace(/\s+/g, "");
  return parole.every((p) => campi.includes(p) || compatti.includes(p));
}

/** Manca quello che serve a stampare un documento vero: l'indirizzo o
 * nessuno dei due codici. Segnalarlo qui, quando ancora non c'è fretta di
 * fatturare, costa meno che scoprirlo davanti al bottone "Scarica il PDF". */
function anagraficaIncompleta(cliente: Cliente): boolean {
  return (
    !cliente.indirizzo.trim() ||
    (!cliente.codice_fiscale.trim() && !cliente.partita_iva.trim())
  );
}

/** La scheda di un cliente, a destra dell'elenco: chi e', come si fattura,
 * cosa manca, e le sue pratiche. Prima era una fisarmonica dentro l'elenco,
 * con il modulo di una pratica nuova sempre spalancato e l'anagrafica vera
 * (indirizzo, codici) che non si vedeva da nessuna parte. */
function SchedaCliente({ cliente, pratiche, dopo, dopoCliente }: SchedaProps) {
  const [nuovaPratica, setNuovaPratica] = useState(false);
  const [correggi, setCorreggi] = useState(false);
  const { utente } = useSessione();
  // Quanto un cliente deve allo studio lo vede chi lavora sui documenti, non chi imputa le ore.
  const vedePagamenti = utente !== null && utente !== undefined && utente.ruolo !== "OPERATORE";
  const incompleta = anagraficaIncompleta(cliente);
  const anno = new Date().getFullYear();
  const regime = [
    cliente.sostituto_imposta ? "con ritenuta d'acconto" : "senza ritenuta d'acconto",
    cliente.pubblica_amministrazione ? "pubblica amministrazione" : "",
    cliente.split_payment ? "scissione dei pagamenti" : "",
    cliente.territorialita === "PRIVATO_UE"
      ? "privato residente in UE"
      : cliente.territorialita === "PRIVATO_EXTRA_UE"
        ? "privato residente fuori UE"
        : "",
  ].filter(Boolean);
  const aperte = pratiche.filter((p) => p.stato === "APERTA");
  const altre = pratiche.filter((p) => p.stato !== "APERTA");

  if (correggi) {
    return (
      <ModuloCliente
        iniziale={cliente}
        chiudi={() => setCorreggi(false)}
        dopo={async () => {
          await dopoCliente();
          await dopo();
          setCorreggi(false);
        }}
      />
    );
  }

  return (
    <article className="scheda-cliente">
      <header className="foglio-testa scheda-testa">
        <div>
          <h2>{cliente.denominazione}</h2>
          <p className="scheda-regime">{regime.join(", ")}</p>
        </div>
        <div className="scheda-comandi">
          <Link
            className="tasto"
            to={`/ore/registro?cliente=${cliente.identificativo}&dal=${anno}-01-01&al=${oggi()}`}
          >
            Vedi le ore
          </Link>
          <button type="button" className="tasto tasto-nudo" onClick={() => setCorreggi(true)}>
            Correggi l'anagrafica
          </button>
        </div>
      </header>

      {incompleta && (
        <p className="riga-avviso riga-avviso-azione">
          <span>
            Manca l'indirizzo o un codice: finché è così, per questo cliente non si stampa
            nessun documento.
          </span>
          <button type="button" className="tasto tasto-nudo" onClick={() => setCorreggi(true)}>
            Completala
          </button>
        </p>
      )}

      <dl className="dati dati-scheda">
        <dt>Indirizzo</dt>
        <dd>{cliente.indirizzo || <span className="mancante">non indicato</span>}</dd>
        <dt>Codice fiscale</dt>
        <dd>
          {/* Basta uno dei due codici: il fiscale manca davvero solo se manca
              anche la partita IVA. */}
          {cliente.codice_fiscale ||
            (cliente.partita_iva ? "non indicato" : <span className="mancante">non indicato</span>)}
        </dd>
        <dt>Partita IVA</dt>
        <dd>{cliente.partita_iva || "nessuna"}</dd>
        <dt>{cliente.pubblica_amministrazione ? "Codice univoco ufficio" : "Recapito SDI"}</dt>
        <dd>
          <RecapitoSdi cliente={cliente} dopo={dopoCliente} />
        </dd>
        {(cliente.codice || cliente.sede) && (
          <>
            <dt>Codice e sede</dt>
            <dd>{[cliente.codice, cliente.sede].filter(Boolean).join(", ")}</dd>
          </>
        )}
      </dl>

      {vedePagamenti && <FatturatoCliente cliente={cliente} />}

      <section className="scheda-sezione">
        <div className="sezione-testa">
          <h3>Pratiche aperte</h3>
          {!nuovaPratica && (
            <button type="button" className="tasto" onClick={() => setNuovaPratica(true)}>
              Nuova pratica
            </button>
          )}
        </div>
        {nuovaPratica && (
          <NuovaPratica
            cliente={cliente}
            chiudi={() => setNuovaPratica(false)}
            dopo={async () => {
              setNuovaPratica(false);
              await dopo();
            }}
          />
        )}
        <Pratiche pratiche={aperte} dopo={dopo} vuote="Nessuna pratica aperta." />
        {altre.length > 0 && (
          <details className="archivio">
            <summary>{conta(altre.length, "pratica sospesa o chiusa", "pratiche sospese o chiuse")}</summary>
            <Pratiche pratiche={altre} dopo={dopo} vuote="" />
          </details>
        )}
      </section>
    </article>
  );
}

/** Il recapito SDI, l'unica parte dell'anagrafica che si cambia dopo la
 * creazione: non si congela sui documenti, quindi cambiarlo non riscrive
 * niente di gia' mandato. */
function RecapitoSdi({ cliente, dopo }: { cliente: Cliente; dopo: () => Promise<void> }) {
  const [modifica, setModifica] = useState(false);
  const [codice, setCodice] = useState(cliente.codice_destinatario);
  const [pec, setPec] = useState(cliente.pec);
  const [errore, setErrore] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);

  const recapito = cliente.codice_destinatario || cliente.pec;

  if (!modifica) {
    return (
      <span className="valore-con-azione">
        {recapito ? recapito : <span className="mancante">non indicato</span>}
        <button type="button" className="tasto tasto-nudo" onClick={() => setModifica(true)}>
          {recapito ? "Cambia" : "Aggiungi"}
        </button>
      </span>
    );
  }

  async function invia(evento: FormEvent) {
    evento.preventDefault();
    setErrore(null);
    setInCorso(true);
    try {
      await anagrafica.cambiaRecapitoSdi(cliente.identificativo, codice, pec);
      setModifica(false);
      await dopo();
    } catch (e) {
      setErrore(e instanceof ErroreApi ? e.message : "Non sono riuscito a salvare.");
    } finally {
      setInCorso(false);
    }
  }

  return (
    <form className="modulo gesto" onSubmit={invia} noValidate>
      <div className="imputa-riga">
        <div className="campo">
          <label htmlFor={`sdi-${cliente.identificativo}`}>
            {cliente.pubblica_amministrazione ? "Codice univoco ufficio" : "Codice destinatario SDI"}
          </label>
          <input
            id={`sdi-${cliente.identificativo}`}
            value={codice}
            maxLength={cliente.pubblica_amministrazione ? 6 : 7}
            onChange={(e) => setCodice(e.target.value)}
          />
        </div>
        {!cliente.pubblica_amministrazione && (
          <div className="campo campo-largo">
            <label htmlFor={`pec-${cliente.identificativo}`}>oppure PEC</label>
            <input
              id={`pec-${cliente.identificativo}`}
              value={pec}
              onChange={(e) => setPec(e.target.value)}
            />
          </div>
        )}
      </div>
      {errore && (
        <p className="errore" role="alert">
          {errore}
        </p>
      )}
      <div className="tasti">
        <button className="tasto tasto-principale" type="submit" disabled={inCorso}>
          {inCorso ? "Sto salvando" : "Salva il recapito"}
        </button>
        <button type="button" className="tasto tasto-nudo" onClick={() => setModifica(false)}>
          Lascia perdere
        </button>
      </div>
    </form>
  );
}

/** Il registro filtrato su una pratica, dall'inizio del suo anno (o da quando
 * e' stata aperta, se prima) a oggi: un bilancio 2025 si lavora anche nel 2026. */
function oreDellaPratica(p: Pratica): string {
  const inizioAnno = `${p.periodo}-01-01`;
  const primo = p.aperta_il !== null && p.aperta_il < inizioAnno ? p.aperta_il : inizioAnno;
  // Una pratica di un anno che deve ancora venire: da oggi, mai una finestra rovesciata.
  const dal = primo > oggi() ? oggi() : primo;
  return `/ore/registro?cliente=${p.cliente.identificativo}&pratica=${p.identificativo}&dal=${dal}&al=${oggi()}&per=giorno`;
}

function Pratiche({
  pratiche,
  dopo,
  vuote,
}: {
  pratiche: Pratica[];
  dopo: () => Promise<void>;
  vuote: string;
}) {
  const [errore, setErrore] = useState<string | null>(null);

  async function cambia(azione: () => Promise<unknown>) {
    setErrore(null);
    try {
      await azione();
      await dopo();
    } catch (e) {
      setErrore(e instanceof ErroreApi ? e.message : "Non sono riuscito a cambiare stato.");
    }
  }

  if (pratiche.length === 0) {
    return vuote ? <p className="aiuto">{vuote}</p> : null;
  }

  return (
    <>
      {errore && (
        <p className="errore" role="alert">
          {errore}
        </p>
      )}
      <table className="tabella">
        <tbody>
          {pratiche.map((p) => (
            <tr key={p.identificativo}>
              <td>
                <span className="pratica-nome">
                  {nomeTipo(p.tipo)} {p.periodo}
                </span>
                {p.descrizione && <span className="narrativa">{p.descrizione}</span>}
                {p.prezzo_concordato !== null && (
                  <span className="narrativa">
                    prezzo concordato {euro(p.prezzo_concordato)}
                  </span>
                )}
                <Link className="collegamento-piccolo" to={oreDellaPratica(p)}>
                  Vedi le ore
                </Link>
              </td>
              <td className="colonna-stato">
                {/* Sotto "Pratiche aperte" dire "aperta" su ogni riga e' rumore:
                    lo stato si scrive solo dove distingue. */}
                {p.stato !== "APERTA" && (
                  <span className={`stato stato-${p.stato.toLowerCase()}`}>
                    {nomeStato(p.stato)}
                  </span>
                )}
              </td>
              <td className="colonna-azione">
                {p.stato === "APERTA" && (
                  <>
                    <button
                      type="button"
                      className="tasto tasto-piccolo"
                      onClick={() => cambia(() => anagrafica.sospendi(p.identificativo))}
                    >
                      Sospendi
                    </button>
                    <button
                      type="button"
                      className="tasto tasto-piccolo"
                      onClick={() =>
                        cambia(() => anagrafica.chiudi(p.identificativo, oggi()))
                      }
                    >
                      Chiudi
                    </button>
                  </>
                )}
                {p.stato !== "APERTA" && (
                  <button
                    type="button"
                    className="tasto tasto-piccolo"
                    onClick={() => cambia(() => anagrafica.riapri(p.identificativo))}
                  >
                    Riapri
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

function NuovaPratica({
  cliente,
  dopo,
  chiudi,
}: {
  cliente: Cliente;
  dopo: () => Promise<void>;
  chiudi: () => void;
}) {
  const [tipo, setTipo] = useState("");
  const [periodo, setPeriodo] = useState(String(new Date().getFullYear()));
  const [descrizione, setDescrizione] = useState("");
  const [responsabile, setResponsabile] = useState("");
  const [prezzo, setPrezzo] = useState("");
  const [errore, setErrore] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);

  async function invia(evento: FormEvent) {
    evento.preventDefault();
    setErrore(null);
    const anno = Number.parseInt(periodo, 10);
    if (!Number.isInteger(anno)) {
      setErrore("Il periodo è un anno, per esempio 2026.");
      return;
    }
    // Vuoto vuol dire "si fattura a tempo", ed e' il caso normale. Scritto
    // male invece si ferma: un prezzo concordato letto storto finisce in
    // parcella storto.
    const concordato = prezzo.trim() === "" ? null : importoDaScritto(prezzo);
    if (prezzo.trim() !== "" && concordato === null) {
      setErrore("Il prezzo concordato è un importo, per esempio 200 oppure 1.200,00.");
      return;
    }
    setInCorso(true);
    try {
      await anagrafica.creaPratica({
        cliente_id: cliente.identificativo,
        tipo,
        periodo: anno,
        descrizione,
        responsabile,
        prezzo_concordato: concordato,
      });
      setTipo("");
      setDescrizione("");
      setResponsabile("");
      setPrezzo("");
      await dopo();
    } catch (e) {
      setErrore(e instanceof ErroreApi ? e.message : "Non sono riuscito a salvare.");
    } finally {
      setInCorso(false);
    }
  }

  return (
    <form className="modulo modulo-stretto modulo-in-scheda" onSubmit={invia} noValidate>
      <div className="imputa-riga">
        <div className="campo campo-largo">
          <label htmlFor={`tipo-${cliente.identificativo}`}>Tipo di lavoro</label>
          <select
            id={`tipo-${cliente.identificativo}`}
            value={tipo}
            required
            onChange={(e) => setTipo(e.target.value)}
          >
            <option value="">Scegli</option>
            {TIPI_PRATICA.map((t) => (
              <option key={t.valore} value={t.valore}>
                {t.nome}
              </option>
            ))}
          </select>
        </div>
        <div className="campo campo-minuti">
          <label htmlFor={`periodo-${cliente.identificativo}`}>Anno</label>
          <input
            id={`periodo-${cliente.identificativo}`}
            type="number"
            value={periodo}
            required
            onChange={(e) => setPeriodo(e.target.value)}
          />
        </div>
      </div>

      <div className="imputa-riga">
        <div className="campo campo-largo">
          <label htmlFor={`desc-${cliente.identificativo}`}>Descrizione</label>
          <input
            id={`desc-${cliente.identificativo}`}
            value={descrizione}
            placeholder="serve a distinguerla da un'altra dello stesso tipo"
            onChange={(e) => setDescrizione(e.target.value)}
          />
        </div>
        <div className="campo">
          <label htmlFor={`resp-${cliente.identificativo}`}>Responsabile</label>
          <input
            id={`resp-${cliente.identificativo}`}
            value={responsabile}
            onChange={(e) => setResponsabile(e.target.value)}
          />
        </div>
      </div>

      <div className="campo">
        <label htmlFor={`prezzo-${cliente.identificativo}`}>
          Prezzo concordato, se c'è
        </label>
        <input
          id={`prezzo-${cliente.identificativo}`}
          inputMode="decimal"
          value={prezzo}
          placeholder="vuoto: si fattura a tempo"
          onChange={(e) => setPrezzo(e.target.value)}
        />
        <p className="aiuto">
          Con un prezzo concordato l'avviso usa quello, una volta sola, e le ore
          servono a sapere quanto è costata davvero la pratica.
        </p>
      </div>

      {errore && (
        <p className="errore" role="alert">
          {errore}
        </p>
      )}

      <div className="tasti">
        <button className="tasto tasto-principale" type="submit" disabled={inCorso}>
          {inCorso ? "Sto salvando" : "Apri la pratica"}
        </button>
        <button type="button" className="tasto tasto-nudo" onClick={chiudi}>
          Lascia perdere
        </button>
      </div>
    </form>
  );
}
